import { useState, useEffect } from 'react'
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  DatePicker,
  Dialog,
  Group,
  Heading,
  Popover,
  DateValue,
} from 'react-aria-components'
import { CalendarDate } from '@internationalized/date'

type AlgoliaDatePickerProps = {
  label: string
  state: number | null
  stateCallback: (date: number | null) => void | null
}

export default function AlgoliaDatePicker({
  label,
  state,
  stateCallback,
}: AlgoliaDatePickerProps): React.ReactNode {
  const [value, setValue] = useState<DateValue | null>(null)

  // Synchronize local value with incoming state prop
  useEffect(() => {
    if (state) {
      // Convert Unix timestamp back to CalendarDate
      const date = new Date(state * 1000)
      const calendarDate = new CalendarDate(
        date.getFullYear(),
        date.getMonth() + 1, // CalendarDate uses 1-based months
        date.getDate(),
      )
      setValue(calendarDate)
    } else {
      setValue(null)
    }
  }, [state])

  return (
    <DatePicker
      value={value}
      onChange={(value) => {
        const { day, month, year } = value || {}
        const date =
          day && month && year ? new Date(year, month - 1, day) : null
        const unixTimestamp = date ? Math.floor(date.getTime() / 1000) : null

        setValue(value)
        stateCallback(unixTimestamp)
      }}>
      <Group className="w-full">
        <Button className="group w-full block p-xs border border-blue-oxford hover:border-gold">
          {() => (
            <div className="flex items-center justify-between">
              <span>
                {value ? `${value.day}/${value.month}/${value.year}` : label}
              </span>

              <svg
                width="19"
                height="20"
                className="text-blue-oxford group-hover:text-gold">
                <use xlinkHref="#icon-datepicker"></use>
              </svg>
            </div>
          )}
        </Button>
      </Group>

      <Popover className="bg-white px-sm rounded-md border border-blue-oxford">
        <Dialog>
          <Calendar>
            <header>
              <Button slot="previous">◀</Button>
              <Heading />
              <Button slot="next">▶</Button>
            </header>

            <CalendarGrid>
              {(date) => (
                <CalendarCell date={date}>
                  {({
                    formattedDate,
                    isSelected,
                    isDisabled,
                    isOutsideMonth,
                    isUnavailable,
                  }) => (
                    <div
                      className={`
                        w-9 h-9 flex items-center justify-center cursor-pointer rounded-full transition-colors
                        ${
                          isSelected
                            ? 'bg-blue-oxford hover:!bg-blue-oxford !text-white font-bold'
                            : ''
                        }
                        ${
                          isDisabled || isOutsideMonth || isUnavailable
                            ? 'text-grey-400 cursor-not-allowed opacity-50 hover:bg-transparent'
                            : 'hover:bg-grey-light'
                        }
                      `}>
                      {formattedDate}
                    </div>
                  )}
                </CalendarCell>
              )}
            </CalendarGrid>
          </Calendar>
        </Dialog>
      </Popover>
    </DatePicker>
  )
}
